// RecordingsList — listado de grabaciones de una clase (o todas).
// Embebido en la sala de clase y en las páginas de "Mis clases grabadas".
import { useEffect, useState } from "react";
import { Film, Trash2, Loader2, Play, Clock, FileText, Mic, ExternalLink, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import HierarchyFilters, { EMPTY_HIER, hierParams } from "@/components/HierarchyFilters";

export default function RecordingsList({ classId = null, canDelete = false, refreshKey = 0 }) {
  const { user } = useAuth();
  const canFilter = !classId && (user?.role === "admin" || user?.role === "franquiciado"); // v5.6.5
  const [hier, setHier] = useState(EMPTY_HIER);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // recording siendo reproducida
  const [loadErr, setLoadErr] = useState(null); // v5.6.3 · un error ya no se muestra como «sin grabaciones»

  const load = async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const params = {};
      if (classId) params.class_id = classId;
      if (canFilter) Object.assign(params, hierParams(hier));
      const { data } = await api.get("/recordings", { params });
      // v5.6.0 · SIEMPRE de la más reciente a la más antigua.
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setItems(list);
    } catch (e) {
      setItems([]);
      const st = e?.response?.status;
      setLoadErr(st ? `No se pudieron cargar las grabaciones (error ${st}).` : "No se pudieron cargar las grabaciones (sin conexión con el servidor).");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [classId, refreshKey, hier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresco: mientras haya grabaciones "processing", recargamos cada 12s
  // para reflejar cuando terminen de procesarse en segundo plano.
  useEffect(() => {
    const anyProcessing = items.some((r) => r.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(() => { load(); }, 12000);
    return () => clearInterval(t);
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // v5.7.5 · busca en Backblaze las grabaciones de los últimos 30 días que no se registraron y las agrega
  const [recovering, setRecovering] = useState(false);
  const canRecover = !classId && ["admin", "franquiciado", "docente"].includes(user?.role);
  const recover = async () => {
    setRecovering(true);
    try {
      const { data } = await api.post("/recordings/reconcile", null, { params: { days: 30 } });
      if (data?.created) toast.success(`Se recuperaron ${data.created} grabación(es).`);
      else toast.info(`Revisión completa (${data?.scanned || 0} clases): no faltaba ninguna grabación.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo revisar las grabaciones");
    } finally { setRecovering(false); }
  };

  const download = async (rec) => {
    try {
      const { data } = await api.get(`/recordings/${rec.id}/download`);
      if (!data?.url) { toast.error("No se pudo generar la descarga"); return; }
      // Navegar directo a la URL firmada (Content-Disposition: attachment).
      // Es más confiable entre navegadores/móvil que un <a download> hacia un
      // recurso de otro origen (donde el atributo download se ignora).
      toast.success("Descarga iniciada");
      window.location.href = data.url;
    } catch {
      toast.error("No se pudo descargar la grabación");
    }
  };

  const remove = async (rec) => {
    if (!window.confirm(`¿Eliminar la grabación "${rec.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/recordings/${rec.id}`);
      toast.success("Grabación eliminada");
      setItems((prev) => prev.filter((x) => x.id !== rec.id));
      if (active?.id === rec.id) setActive(null);
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const fmtDur = (s) => {
    if (!s) return "--:--";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}:${sec.toString().padStart(2, "0")}`;
    return `0:${sec.toString().padStart(2, "0")}`;
  };

  const fmtSize = (b) => {
    if (!b) return "—";
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="jasq-card p-4 space-y-3" data-testid="recordings-list">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Grabaciones</p>
          <h3 className="font-display text-lg font-extrabold text-[#1F2547] inline-flex items-center gap-2">
            <Film size={16} className="text-[#A43790]" />
            {classId ? "De esta clase" : "Todas mis grabaciones"}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {canRecover && (
            <button type="button" onClick={recover} disabled={recovering} data-testid="recordings-recover"
              title="Busca en el almacenamiento las grabaciones de los últimos 30 días que no aparecen y las agrega"
              className="jasq-btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-60">
              <RefreshCw size={12} className={recovering ? "animate-spin" : ""} /> {recovering ? "Revisando…" : "Recuperar grabaciones"}
            </button>
          )}
          <span className="text-xs text-[#6E7591]" data-testid="recordings-count">{items.length} grabación(es)</span>
        </div>
      </div>

      {canFilter && <HierarchyFilters value={hier} onChange={setHier} />}

      {loading ? (
        <div className="py-8 text-center text-[#6E7591] text-sm inline-flex items-center justify-center w-full">
          <Loader2 size={16} className="animate-spin mr-2" /> Cargando…
        </div>
      ) : loadErr ? (
        <div className="py-8 text-center text-sm text-[#A52258]" data-testid="recordings-error">
          {loadErr}
          <div><button type="button" onClick={load} className="mt-2 jasq-btn-ghost text-xs">Reintentar</button></div>
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-[#6E7591] text-sm" data-testid="recordings-empty">
          No hay grabaciones todavía.
          {canDelete && <p className="text-[11px] mt-1">Pulsa <b>Grabar</b> arriba para empezar.</p>}
        </div>
      ) : (
        <ul className="divide-y divide-[#E1E5F0]">
          {items.map((rec) => {
            const isProcessing = rec.status === "processing";
            const isFailed = rec.status === "failed";
            const isAudioOnly = rec.status === "error_audio_only";
            const isReady = !isProcessing && !isFailed && !isAudioOnly;
            return (
            <li key={rec.id} className="py-2 flex items-center gap-3" data-testid={`rec-${rec.id}`}>
              {isProcessing ? (
                <span className="w-9 h-9 rounded-full bg-[#FFF3D6] text-[#B8860B] inline-flex items-center justify-center shrink-0" title="Procesando" data-testid={`rec-processing-${rec.id}`}>
                  <Loader2 size={14} className="animate-spin" />
                </span>
              ) : isAudioOnly ? (
                <span className="w-9 h-9 rounded-full bg-[#FFF0E6] text-[#C65D00] inline-flex items-center justify-center shrink-0" title="Solo audio — sin video" data-testid={`rec-audio-only-${rec.id}`}>
                  <Mic size={14} />
                </span>
              ) : isFailed ? (
                <span className="w-9 h-9 rounded-full bg-[#FDE8EF] text-[#A52258] inline-flex items-center justify-center shrink-0" title="Falló el procesamiento" data-testid={`rec-failed-${rec.id}`}>
                  <Film size={14} />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setActive(active?.id === rec.id ? null : rec)}
                  className="w-9 h-9 rounded-full bg-[#A43790] text-white inline-flex items-center justify-center shrink-0"
                  data-testid={`rec-play-${rec.id}`}
                  title="Reproducir"
                >
                  <Play size={14} />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#1F2547] truncate">
                  {rec.title}
                  {isProcessing && <span className="ml-2 text-[10px] font-extrabold text-[#B8860B] bg-[#FFF3D6] px-1.5 py-0.5 rounded-full align-middle">Procesando…</span>}
                  {isAudioOnly && <span className="ml-2 text-[10px] font-extrabold text-[#C65D00] bg-[#FFF0E6] px-1.5 py-0.5 rounded-full align-middle">Solo audio</span>}
                  {isFailed && <span className="ml-2 text-[10px] font-extrabold text-[#A52258] bg-[#FDE8EF] px-1.5 py-0.5 rounded-full align-middle">Error</span>}
                </p>
                <p className="text-[11px] text-[#6E7591] inline-flex items-center gap-2 flex-wrap">
                  <span><Clock size={10} className="inline mr-0.5" /> {fmtDur(rec.duration_seconds)}</span>
                  <span>· {fmtSize(rec.size_bytes)}</span>
                  <span>· {new Date(rec.created_at).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" })}</span>
                </p>
                {isAudioOnly && (
                  <p className="text-[10px] text-[#C65D00] mt-0.5" data-testid={`rec-audio-only-msg-${rec.id}`}>
                    La grabación solo capturó audio. Intenta grabar con VP9 o verifica los permisos de captura de pantalla.
                  </p>
                )}
                {/* PDFs del pizarrón asociados — al lado del video */}
                {Array.isArray(rec.whiteboard_pdfs) && rec.whiteboard_pdfs.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`rec-${rec.id}-pdfs`}>
                    {rec.whiteboard_pdfs.map((pdf) => (
                      <a
                        key={pdf.id}
                        href={pdf.pdf_url}
                        target="_blank"
                        rel="noreferrer"
                        download={pdf.filename}
                        data-testid={`rec-${rec.id}-pdf-${pdf.id}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E8EEFB] text-[#2C49A0] text-[10px] font-extrabold hover:bg-[#2C49A0] hover:text-white transition-colors"
                        title={`Notas del pizarrón: ${pdf.filename}`}
                      >
                        <FileText size={10} /> Pizarrón PDF
                      </a>
                    ))}
                  </div>
                )}
                {rec.google_drive_link && (
                  <a href={rec.google_drive_link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E8EEFB] text-[#2C49A0] text-[10px] font-extrabold hover:bg-[#2C49A0] hover:text-white transition-colors"
                    data-testid={`rec-drive-${rec.id}`}>
                    <ExternalLink size={10} /> Ver en Drive
                  </a>
                )}
              </div>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => remove(rec)}
                  data-testid={`rec-delete-${rec.id}`}
                  className="text-[#A52258] hover:bg-[#FFE9F0] rounded p-1.5"
                  title="Eliminar"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {active && (
        <div className="rounded-xl bg-black overflow-hidden border-2 border-[#1F2547]" data-testid="rec-player">
          <video
            controls
            autoPlay
            src={active.video_url || active.cloudinary_url}
            className="w-full max-h-[60vh] block"
            data-testid="rec-video-player"
          />
          <div className="px-3 py-2 bg-[#1F2547] text-white text-xs font-bold flex items-center justify-between">
            <span className="truncate">{active.title}</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => download(active)} data-testid={`rec-download-${active.id}`} className="text-[10px] underline">
                Descargar
              </button>
              <button type="button" onClick={() => setActive(null)} className="text-[10px] underline">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
